import React, { useState, createElement } from 'react';
import { formatCurrency } from '../utils/currencyFormatter';
import { CheckIcon, XIcon, AlertTriangleIcon, SearchIcon } from 'lucide-react';
export function POMatching({
  invoices,
  locale = 'en-US'
}) {
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  // Sample PO data
  const purchaseOrders = [{
    id: 'PO-2023-001',
    vendor: 'Office Depot',
    date: '2023-10-10',
    total: 1250.75,
    lineItems: [{
      id: 1,
      description: 'Paper supplies',
      quantity: 10,
      unitPrice: 25.5,
      total: 255.0
    }, {
      id: 2,
      description: 'Printer ink',
      quantity: 5,
      unitPrice: 85.75,
      total: 428.75
    }, {
      id: 3,
      description: 'Desk organizers',
      quantity: 15,
      unitPrice: 37.8,
      total: 567.0
    }]
  }, {
    id: 'PO-2023-002',
    vendor: 'Dell Technologies',
    date: '2023-10-05',
    total: 5430.0,
    lineItems: [{
      id: 1,
      description: 'Laptops',
      quantity: 2,
      unitPrice: 1250.0,
      total: 2500.0
    }, {
      id: 2,
      description: 'Monitors',
      quantity: 4,
      unitPrice: 350.0,
      total: 1400.0
    }, {
      id: 3,
      description: 'Docking stations',
      quantity: 2,
      unitPrice: 765.0,
      total: 1530.0
    }]
  }];
  // Sample matching results
  const matchingResults = {
    'INV-2023-001': {
      poId: 'PO-2023-001',
      status: 'partial',
      lineItemMatches: [{
        invoiceLineId: 1,
        poLineId: 1,
        status: 'matched',
        difference: 0
      }, {
        invoiceLineId: 2,
        poLineId: 2,
        status: 'partial',
        difference: 25.0
      }, {
        invoiceLineId: 3,
        poLineId: 3,
        status: 'no_match',
        difference: 150.0
      }]
    }
  };
  const handleSelectInvoice = invoice => {
    setSelectedInvoice(invoice);
  };
  const getMatchingPO = invoice => {
    if (!invoice.poReference) return null;
    return purchaseOrders.find(po => po.id === invoice.poReference);
  };
  const getMatchStatus = status => {
    switch (status) {
      case 'matched':
        return {
          icon: CheckIcon,
          color: 'text-green-600',
          text: 'Matched',
          bgColor: 'bg-green-50'
        };
      case 'partial':
        return {
          icon: AlertTriangleIcon,
          color: 'text-yellow-600',
          text: 'Partial Match',
          bgColor: 'bg-yellow-50'
        };
      default:
        return {
          icon: XIcon,
          color: 'text-red-600',
          text: 'No Match',
          bgColor: 'bg-red-50'
        };
    }
  };
  return <div data-id="element-1012">
      <div className="mb-8" data-id="element-1013">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-1014">PO Matching</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-1015">
          Match invoices against purchase orders
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1016">
        {/* Invoices List */}
        <div className="lg:col-span-1 bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-1017">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-1018">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-1019">
              Pending Invoices
            </h3>
          </div>
          <div className="px-4 py-3 border-b border-gray-200" data-id="element-1020">
            <div className="relative rounded-md shadow-sm" data-id="element-1021">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1022">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1023" />
              </div>
              <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="Search invoices" data-id="element-1024" />
            </div>
          </div>
          <ul className="divide-y divide-gray-200 max-h-[60vh] overflow-y-auto" data-id="element-1025">
            {invoices.map(invoice => <li key={invoice.id} className={`px-4 py-4 hover:bg-gray-50 cursor-pointer ${selectedInvoice?.id === invoice.id ? 'bg-indigo-50' : ''}`} onClick={() => handleSelectInvoice(invoice)} data-id="element-1026">
                <div className="flex justify-between" data-id="element-1027">
                  <div data-id="element-1028">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1029">
                      {invoice.invoiceNumber}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-1030">{invoice.supplier}</p>
                  </div>
                  <div className="text-right" data-id="element-1031">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1032">
                      {formatCurrency(invoice.amount, invoice.currency, locale)}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-1033">
                      {invoice.invoiceDate}
                    </p>
                  </div>
                </div>
                {invoice.poReference && <div className="mt-2 flex items-center" data-id="element-1034">
                    <span className="text-xs text-gray-500 mr-2" data-id="element-1035">
                      PO: {invoice.poReference}
                    </span>
                    {invoice.matchingStatus === 'matched' ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800" data-id="element-1036">
                        Matched
                      </span> : invoice.matchingStatus === 'partial' ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800" data-id="element-1037">
                        Partial
                      </span> : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800" data-id="element-1038">
                        No Match
                      </span>}
                  </div>}
              </li>)}
          </ul>
        </div>
        {/* Matching Details */}
        <div className="lg:col-span-2" data-id="element-1039">
          {selectedInvoice ? <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-1040">
              <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-1041">
                <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-1042">
                  Matching Details
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-1043">
                  {selectedInvoice.invoiceNumber} - {selectedInvoice.supplier}
                </p>
              </div>
              {selectedInvoice.poReference ? <div data-id="element-1044">
                  {/* Header Comparison */}
                  <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-1045">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1046">
                      <div data-id="element-1047">
                        <h4 className="text-sm font-medium text-gray-500" data-id="element-1048">
                          Invoice
                        </h4>
                        <div className="mt-2 bg-gray-50 p-3 rounded-md" data-id="element-1049">
                          <p className="text-sm text-gray-900" data-id="element-1050">
                            Number: {selectedInvoice.invoiceNumber}
                          </p>
                          <p className="text-sm text-gray-900" data-id="element-1051">
                            Date: {selectedInvoice.invoiceDate}
                          </p>
                          <p className="text-sm text-gray-900" data-id="element-1052">
                            Amount:{' '}
                            {formatCurrency(selectedInvoice.amount, selectedInvoice.currency, locale)}
                          </p>
                        </div>
                      </div>
                      <div data-id="element-1053">
                        <h4 className="text-sm font-medium text-gray-500" data-id="element-1054">
                          Purchase Order
                        </h4>
                        <div className="mt-2 bg-gray-50 p-3 rounded-md" data-id="element-1055">
                          {getMatchingPO(selectedInvoice) ? <>
                              <p className="text-sm text-gray-900" data-id="element-1056">
                                Number: {getMatchingPO(selectedInvoice).id}
                              </p>
                              <p className="text-sm text-gray-900" data-id="element-1057">
                                Date: {getMatchingPO(selectedInvoice).date}
                              </p>
                              <p className="text-sm text-gray-900" data-id="element-1058">
                                Amount:{' '}
                                {formatCurrency(getMatchingPO(selectedInvoice).total, 'USD', locale)}
                              </p>
                            </> : <p className="text-sm text-gray-500" data-id="element-1059">
                              No matching PO found
                            </p>}
                        </div>
                      </div>
                    </div>
                    {/* Matching Status */}
                    {selectedInvoice.matchingStatus && <div className="mt-4" data-id="element-1060">
                        <div className={`p-4 rounded-md ${getMatchStatus(selectedInvoice.matchingStatus).bgColor}`} data-id="element-1061">
                          <div className="flex" data-id="element-1062">
                            <div className="flex-shrink-0" data-id="element-1063">
                              {createElement(getMatchStatus(selectedInvoice.matchingStatus).icon, {
                        className: `h-5 w-5 ${getMatchStatus(selectedInvoice.matchingStatus).color}`,
                        'aria-hidden': 'true'
                      })}
                            </div>
                            <div className="ml-3" data-id="element-1064">
                              <h3 className={`text-sm font-medium ${getMatchStatus(selectedInvoice.matchingStatus).color}`} data-id="element-1065">
                                {getMatchStatus(selectedInvoice.matchingStatus).text}
                              </h3>
                              <div className="mt-2 text-sm" data-id="element-1066">
                                <p className="text-gray-700" data-id="element-1067">
                                  {selectedInvoice.matchingStatus === 'matched' ? 'All line items match the purchase order.' : selectedInvoice.matchingStatus === 'partial' ? 'Some line items have discrepancies with the purchase order.' : 'No matching line items found.'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>}
                  </div>
                  {/* Line Items Comparison */}
                  {getMatchingPO(selectedInvoice) && matchingResults[selectedInvoice.id] && <div className="px-4 py-5 sm:px-6" data-id="element-1068">
                        <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-1069">
                          Line Item Matching
                        </h4>
                        <div className="overflow-x-auto" data-id="element-1070">
                          <table className="min-w-full divide-y divide-gray-200" data-id="element-1071">
                            <thead className="bg-gray-50" data-id="element-1072">
                              <tr data-id="element-1073">
                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1074">
                                  Item
                                </th>
                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1075">
                                  PO Qty
                                </th>
                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1076">
                                  Inv Qty
                                </th>
                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1077">
                                  PO Price
                                </th>
                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1078">
                                  Inv Price
                                </th>
                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1079">
                                  Difference
                                </th>
                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1080">
                                  Status
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1081">
                              {matchingResults[selectedInvoice.id].lineItemMatches.map((match, index) => {
                      const poLine = getMatchingPO(selectedInvoice).lineItems.find(line => line.id === match.poLineId);
                      return <tr key={index} className={`
                                  ${match.status === 'matched' ? 'bg-green-50' : match.status === 'partial' ? 'bg-yellow-50' : 'bg-red-50'}
                                `} data-id="element-1082">
                                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1083">
                                      {poLine.description}
                                    </td>
                                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1084">
                                      {poLine.quantity}
                                    </td>
                                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1085">
                                      {poLine.quantity}
                                    </td>
                                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1086">
                                      {formatCurrency(poLine.unitPrice, 'USD', locale)}
                                    </td>
                                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1087">
                                      {formatCurrency(poLine.unitPrice + match.difference / poLine.quantity, 'USD', locale)}
                                    </td>
                                    <td className={`px-3 py-4 whitespace-nowrap text-sm ${match.difference === 0 ? 'text-gray-500' : 'text-red-500 font-medium'}`} data-id="element-1088">
                                      {match.difference === 0 ? '-' : formatCurrency(match.difference, 'USD', locale)}
                                    </td>
                                    <td className="px-3 py-4 whitespace-nowrap" data-id="element-1089">
                                      {match.status === 'matched' ? <span className="inline-flex items-center text-green-600" data-id="element-1090">
                                          <CheckIcon className="h-4 w-4 mr-1" data-id="element-1091" />{' '}
                                          Matched
                                        </span> : match.status === 'partial' ? <span className="inline-flex items-center text-yellow-600" data-id="element-1092">
                                          <AlertTriangleIcon className="h-4 w-4 mr-1" data-id="element-1093" />{' '}
                                          Partial
                                        </span> : <span className="inline-flex items-center text-red-600" data-id="element-1094">
                                          <XIcon className="h-4 w-4 mr-1" data-id="element-1095" /> No
                                          Match
                                        </span>}
                                    </td>
                                  </tr>;
                    })}
                            </tbody>
                          </table>
                        </div>
                        {/* Actions */}
                        <div className="mt-6 flex justify-end space-x-3" data-id="element-1096">
                          <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1097">
                            Reject
                          </button>
                          <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1098">
                            Approve with Exceptions
                          </button>
                        </div>
                      </div>}
                </div> : <div className="px-4 py-5 sm:p-6 text-center" data-id="element-1099">
                  <p className="text-gray-500" data-id="element-1100">
                    This invoice is not linked to any purchase order.
                  </p>
                  <button type="button" className="mt-3 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1101">
                    Link to PO
                  </button>
                </div>}
            </div> : <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6 text-center" data-id="element-1102">
              <p className="text-gray-500" data-id="element-1103">
                Select an invoice to view matching details
              </p>
            </div>}
        </div>
      </div>
    </div>;
}